import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, Manifest } from "../api";

// MARKET INTEL — what is changing beneath the price action, why it matters,
// and who needs to know. Three questions in three seconds: WHAT CHANGED (the
// ranked event stream), WHERE IS EVERYTHING (the structure/entry quadrant +
// the sector map), and for one name, WHERE IS PRICE AGAINST PARTICIPANT COST
// BASIS (the VWAP ribbon, the term-structure curve, the timeline). Every
// number is the engine's; the analyst paragraph is the only prose a model
// wrote, and it was written from these same numbers.

type Row = {
  symbol: string; sector: string | null; roles: string[]; held_by: string[]; price: number | null; as_of: string;
  structure: number; entry: number; d_structure: number; d_entry: number; relvol: number | null; alignment: string | null;
  stage: string | null; priority: string; compression_pctile: number | null; daily_compression_pctile: number | null;
  above: Record<string, boolean | null>; slopes: Record<string, string | null>; key_level: KeyLevel | null; why: string | null;
  trajectory: [string, number, number][];
};
type KeyLevel = { kind: string; level: number; members: string[]; dist_atr: number; side: string };
type Sector = { sector: string; n: number; etf: string | null; structure: number; entry: number; d_structure: number;
  breadth: Record<string, number>; improving: number; deteriorating: number };
type Curve = { n: number; now: { h: string; rel_pct: number | null }[]; prior: { h: string; rel_pct: number | null }[] };
type MapResp = { as_of: string | null; rows: Row[]; sectors: Sector[]; breadth: Record<string, number>; indices: Row[]; curves?: Record<string, Curve> };
type Ev = { id: string; symbol: string; at: string; kind: string; significance: number; text: string; detail: Record<string, unknown>;
  audience: string[]; price_at: number | null; fwd_1d: number | null; fwd_5d: number | null; note: string | null };
type Bar = { t: string; o: number; h: number; l: number; c: number; v: number };
type Chart = { minutes: Bar[]; intraday: Record<string, (number | null)[]>; daily: Bar[]; daily_vwaps: Record<string, (number | null)[]>;
  daily_levels: Record<string, number | null>; volume_profile?: number[] };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type State = any;
type Sec = { symbol: string; state: State; as_of: string | null; score_path: [string, number, number][]; timeline: Ev[]; chart?: Chart; chart_error?: string };
type Brief = { kind: string; day?: string; text: string; data: Record<string, unknown> | null; created_at?: string };
type Research = { days: number; events: number; graded_5d: number; kinds: { kind: string; direction: number; n: number; graded_1d: number;
  graded_5d: number; mean_1d: number | null; mean_5d: number | null; hit_1d?: number; hit_5d?: number }[] };
type Card = { symbol: string; shape?: string; chart?: string | null; structure?: number; entry?: number; commentary?: string;
  prediction?: { text: string; confirms?: string; contradicts?: string; direction?: string; confidence?: number } | null };
type Analogs = { symbol: string; pattern?: Record<string, unknown>; sample: number; mean_fwd_pct: number | null; positive_pct: number | null;
  matches: { symbol: string; day: string; structure: number; entry: number; fwd_pct: number | null; structure_later: number | null }[] };

const SECTOR_NAMES: Record<string, string> = {
  technology: "Technology", financials: "Financials", healthcare: "Health Care", energy: "Energy", consumer_disc: "Consumer Disc.",
  consumer_staples: "Staples", industrials: "Industrials", materials: "Materials", communications: "Communications",
  utilities: "Utilities", real_estate: "Real Estate", index: "Index",
};
const INTRADAY = ["30m", "60m", "90m", "120m", "360m", "session"];
const DAILY = ["5d", "30d", "60d", "90d", "120d", "360d"];
const ORDER = [...INTRADAY, ...DAILY];
const RIBBON_COLOR: Record<string, string> = {
  "30m": "#f8fafc", "60m": "#e2e8f0", "90m": "#cbd5e1", "120m": "#94a3b8", "360m": "#64748b", session: "#fbbf24",
  "5d": "#38bdf8", "30d": "#22d3ee", "60d": "#2dd4bf", "90d": "#4ade80", "120d": "#a3e635", "360d": "#f472b6",
};
const KIND_LABEL: Record<string, string> = {
  reclaim: "RECLAIM", loss: "LOSS", slope_up: "SLOPE ↑", slope_down: "SLOPE ↓", alignment_up: "ALIGN ↑", alignment_down: "ALIGN ↓",
  structure_jump: "STRUCTURE ↑", structure_drop: "STRUCTURE ↓", entry_improved: "ENTRY ↑", extended: "EXTENDED", relvol: "VOLUME",
  compression: "COMPRESSION", compression_break_up: "BREAK ↑", compression_break_down: "BREAK ↓", retest_held: "RETEST HELD",
  retest_failed: "RETEST FAILED", weak_volume: "WEAK VOLUME", migration_up: "COST BASIS ↑", migration_down: "COST BASIS ↓", sector_sync: "SECTOR",
};

const et = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", timeZone: "America/New_York", hour12: false });
const etDay = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/New_York" });
const sig = (n: number) => n >= 80 ? "text-up" : n >= 60 ? "text-amber-300" : "text-slate-400";
const scoreTone = (n: number) => n >= 75 ? "text-up" : n >= 55 ? "text-amber-300" : n >= 35 ? "text-slate-300" : "text-down";
const fmt = (n: number | null | undefined, d = 2) => n == null || Number.isNaN(n) ? "—" : n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
const slopeGlyph = (s: string | null | undefined) => s === "strongly rising" ? "⇈" : s === "rising" ? "↑" : s === "falling" ? "↓" : s === "strongly falling" ? "⇊" : s ? "→" : "·";

export default function Intel(_p: { manifest: Manifest }) {
  const [map, setMap] = useState<MapResp | null>(null);
  const [events, setEvents] = useState<Ev[]>([]);
  const [audience, setAudience] = useState<string>("");
  const [sectorFilter, setSectorFilter] = useState<string>("");
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<"now" | "brief" | "research">("now");
  const [scanning, setScanning] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [m, e] = await Promise.all([api.get<MapResp>("/api/intel/map"), api.get<Ev[]>("/api/intel/changes?hours=10&limit=80")]);
      setMap(m); setEvents(e); setErr(null);
    } catch (x) { setErr(String((x as Error).message || x)); }
  }, []);
  useEffect(() => { void load(); const id = setInterval(() => void load(), 60_000); return () => clearInterval(id); }, [load]);

  const scanNow = async () => {
    setScanning(true);
    try { await api.post("/api/intel/scan"); await load(); } catch (x) { setErr(String((x as Error).message || x)); } finally { setScanning(false); }
  };

  const rows = useMemo(() => (map?.rows ?? []).filter(r => !sectorFilter || r.sector === sectorFilter), [map, sectorFilter]);
  const shownEvents = useMemo(() => events.filter(e => (!audience || e.audience.includes(audience)) &&
    (!sectorFilter || (map?.rows ?? []).some(r => r.symbol === e.symbol && r.sector === sectorFilter))), [events, audience, sectorFilter, map]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Market Intel</h1>
          <p className="text-sm text-slate-500">
            Traders watch trades. This watches the market. {map?.as_of ? <>Last scan {etDay(map.as_of)} {et(map.as_of)} ET · {map.rows.length} names</> : "No scan yet."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <nav className="flex rounded-lg border border-edge overflow-hidden text-sm">
            {(["now", "brief", "research"] as const).map(t => (
              <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 ${tab === t ? "bg-panel text-slate-100" : "text-slate-500 hover:text-slate-300"}`}>
                {t === "now" ? "What changed" : t === "brief" ? "Briefs" : "Research"}
              </button>
            ))}
          </nav>
          <button className="btn" onClick={scanNow} disabled={scanning}>{scanning ? "Scanning…" : "Scan now"}</button>
        </div>
      </header>
      {err && <div className="card border-down/50 text-down text-sm">{err}</div>}

      {map && <Breadth breadth={map.breadth} indices={map.indices} onPick={setSelected} />}

      {tab === "now" && (
        <>
          <div className="grid gap-4 xl:grid-cols-[minmax(0,5fr)_minmax(0,4fr)]">
            <section className="card space-y-2 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h2 className="label !mb-0">What changed · ranked by significance</h2>
                <select className="input !w-auto !py-1 text-xs" value={audience} onChange={e => setAudience(e.target.value)}>
                  <option value="">everyone</option>
                  <option value="equity desk">equity desk</option>
                  <option value="growth book">growth book</option>
                  <option value="crypto sleeve">crypto sleeve</option>
                  <option value="show">the show</option>
                  <option value="owner">owner</option>
                </select>
              </div>
              {shownEvents.length === 0 && <p className="text-sm text-slate-500">Nothing has changed in the window. That is also intelligence.</p>}
              <ul className="divide-y divide-edge max-h-[520px] overflow-y-auto">
                {shownEvents.map(e => (
                  <li key={e.id} className="py-2 flex gap-3 items-start cursor-pointer hover:bg-ink/40 rounded px-1" onClick={() => setSelected(e.symbol.split(",")[0])}>
                    <span className={`w-8 text-right font-mono text-sm tabular-nums ${sig(e.significance)}`}>{e.significance}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="font-semibold">{e.symbol.length > 14 ? e.symbol.split(",").slice(0, 3).join(", ") + "…" : e.symbol}</span>
                        <span className="text-[10px] tracking-wider text-slate-500">{KIND_LABEL[e.kind] ?? e.kind}</span>
                        <span className="text-xs text-slate-600 tabular-nums">{et(e.at)}</span>
                      </div>
                      <p className="text-sm text-slate-300">{e.text}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {e.audience.map(a => <span key={a} className="badge bg-ink text-slate-500 !font-normal">{a}</span>)}
                        {e.fwd_1d != null && <span className={`badge bg-ink !font-normal ${e.fwd_1d >= 0 ? "text-up" : "text-down"}`}>+1d {fmt(e.fwd_1d)}%</span>}
                        {e.note && <span className="text-xs text-amber-200/80">analyst: {e.note}</span>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
            <section className="card min-w-0">
              <div className="flex items-center justify-between">
                <h2 className="label !mb-0">Structure × Entry · where everything is, and where it came from</h2>
                {sectorFilter && <button className="text-xs text-slate-500 hover:text-slate-300" onClick={() => setSectorFilter("")}>clear {SECTOR_NAMES[sectorFilter]}</button>}
              </div>
              <Quadrant rows={rows} onPick={setSelected} selected={selected} />
            </section>
          </div>

          {map && <SectorMap sectors={map.sectors} active={sectorFilter} onPick={s => setSectorFilter(s === sectorFilter ? "" : s)} />}

          {map?.curves && <AggregateCurves curves={map.curves} sector={sectorFilter} />}

          <Ask />

          {selected && <SecurityPanel symbol={selected} row={map?.rows.find(r => r.symbol === selected) ?? null} onClose={() => setSelected(null)} />}

          <UniverseTable rows={rows} onPick={setSelected} />
        </>
      )}
      {tab === "brief" && <Briefs />}
      {tab === "research" && <ResearchPanel />}
    </div>
  );
}

// ── breadth strip ───────────────────────────────────────────────────────────
function Breadth({ breadth, indices, onPick }: { breadth: Record<string, number>; indices: Row[]; onPick: (s: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {DAILY.slice(1).map(h => (
        <div key={h} className="card !py-2 !px-3 flex items-baseline gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">above {h}</span>
          <span className={`font-mono tabular-nums ${breadth[h] >= 60 ? "text-up" : breadth[h] <= 40 ? "text-down" : "text-slate-200"}`}>{breadth[h] ?? "—"}%</span>
        </div>
      ))}
      <div className="card !py-2 !px-3 flex items-baseline gap-2">
        <span className="text-[10px] uppercase tracking-wider text-slate-500">30d rising</span>
        <span className="font-mono tabular-nums">{breadth.rising_30d ?? "—"}%</span>
      </div>
      {indices.map(r => (
        <button key={r.symbol} onClick={() => onPick(r.symbol)} className="card !py-2 !px-3 flex items-baseline gap-2 hover:border-accent text-left">
          <span className="font-semibold">{r.symbol}</span>
          <span className={`font-mono tabular-nums text-sm ${scoreTone(r.structure)}`}>{r.structure}</span>
          <span className="text-slate-600">/</span>
          <span className={`font-mono tabular-nums text-sm ${scoreTone(r.entry)}`}>{r.entry}</span>
          <span className={`text-xs tabular-nums ${r.d_structure > 0 ? "text-up" : r.d_structure < 0 ? "text-down" : "text-slate-500"}`}>{r.d_structure > 0 ? "+" : ""}{r.d_structure}</span>
        </button>
      ))}
    </div>
  );
}

// ── quadrant with trajectories ──────────────────────────────────────────────
function Quadrant({ rows, onPick, selected }: { rows: Row[]; onPick: (s: string) => void; selected: string | null }) {
  const W = 520, H = 360, P = 28;
  const x = (s: number) => P + (s / 100) * (W - 2 * P);
  const y = (e: number) => H - P - (e / 100) * (H - 2 * P);
  const hue = (sec: string | null) => {
    const keys = Object.keys(SECTOR_NAMES);
    const i = Math.max(0, keys.indexOf(sec ?? ""));
    return `hsl(${(i * 37) % 360} 60% 60%)`;
  };
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-[640px] mx-auto" role="img" aria-label="Structure versus entry quality">
        <rect x={x(50)} y={y(100)} width={x(100) - x(50)} height={y(50) - y(100)} fill="rgba(74,222,128,0.05)" />
        <rect x={x(50)} y={y(50)} width={x(100) - x(50)} height={y(0) - y(50)} fill="rgba(251,191,36,0.05)" />
        <line x1={x(50)} x2={x(50)} y1={y(0)} y2={y(100)} stroke="#334155" strokeDasharray="3 3" />
        <line x1={x(0)} x2={x(100)} y1={y(50)} y2={y(50)} stroke="#334155" strokeDasharray="3 3" />
        <text x={x(75)} y={y(100) + 12} textAnchor="middle" className="fill-slate-500" fontSize="10">STRONG · EFFICIENT</text>
        <text x={x(75)} y={y(0) - 4} textAnchor="middle" className="fill-slate-500" fontSize="10">STRONG · EXTENDED</text>
        <text x={x(25)} y={y(100) + 12} textAnchor="middle" className="fill-slate-500" fontSize="10">CHEAP · UNPROVEN</text>
        <text x={x(25)} y={y(0) - 4} textAnchor="middle" className="fill-slate-500" fontSize="10">LOW PRIORITY</text>
        <text x={W / 2} y={H - 4} textAnchor="middle" className="fill-slate-400" fontSize="10">Structure Score →</text>
        <text x={10} y={H / 2} textAnchor="middle" transform={`rotate(-90 10 ${H / 2})`} className="fill-slate-400" fontSize="10">Entry Quality →</text>
        {rows.map(r => {
          const tr = r.trajectory.length ? r.trajectory : [[r.as_of, r.structure, r.entry] as [string, number, number]];
          const pts = tr.map(([, s, e]) => `${x(s).toFixed(1)},${y(e).toFixed(1)}`).join(" ");
          const rad = 3 + Math.min(6, Math.max(0, ((r.relvol ?? 1) - 0.8) * 3));
          const isSel = r.symbol === selected;
          return (
            <g key={r.symbol} className="cursor-pointer" onClick={() => onPick(r.symbol)}>
              <title>{`${r.symbol} · structure ${r.structure} (${r.d_structure >= 0 ? "+" : ""}${r.d_structure}) · entry ${r.entry} · relvol ${fmt(r.relvol)}`}</title>
              {tr.length > 1 && <polyline points={pts} fill="none" stroke={hue(r.sector)} strokeOpacity="0.45" strokeWidth={isSel ? 2 : 1} />}
              <circle cx={x(r.structure)} cy={y(r.entry)} r={isSel ? rad + 2 : rad} fill={hue(r.sector)} fillOpacity={isSel ? 1 : 0.8} stroke={isSel ? "#fff" : "none"} />
              {(isSel || r.priority === "immediate") && <text x={x(r.structure) + rad + 2} y={y(r.entry) + 3} fontSize="10" className="fill-slate-200">{r.symbol}</text>}
            </g>
          );
        })}
      </svg>
      <p className="text-xs text-slate-500 text-center">Tail = today's path. Bubble = relative volume. Labels = immediate priority.</p>
    </div>
  );
}

// ── sector map ──────────────────────────────────────────────────────────────
function SectorMap({ sectors, active, onPick }: { sectors: Sector[]; active: string; onPick: (s: string) => void }) {
  return (
    <section>
      <h2 className="label">Sector map · cost-basis alignment, change since the open</h2>
      <div className="grid gap-2 grid-cols-2 md:grid-cols-4 xl:grid-cols-6">
        {sectors.map(s => (
          <button key={s.sector} onClick={() => onPick(s.sector)}
            className={`card !p-3 text-left hover:border-accent ${active === s.sector ? "border-accent" : ""}`}>
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-semibold">{SECTOR_NAMES[s.sector] ?? s.sector}</span>
              <span className="text-xs text-slate-500">{s.etf}</span>
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className={`font-mono text-lg tabular-nums ${scoreTone(s.structure)}`}>{s.structure}</span>
              <span className={`text-xs tabular-nums ${s.d_structure > 0 ? "text-up" : s.d_structure < 0 ? "text-down" : "text-slate-500"}`}>{s.d_structure > 0 ? "+" : ""}{s.d_structure}</span>
              <span className="text-xs text-slate-500 ml-auto">entry {s.entry}</span>
            </div>
            <div className="flex gap-0.5 mt-2" title="breadth above 30d/60d/90d/120d/360d">
              {["30d", "60d", "90d", "120d", "360d"].map(h => (
                <div key={h} className="flex-1 h-1.5 rounded-sm bg-ink overflow-hidden"><div className="h-full bg-accent/70" style={{ width: `${s.breadth[h] ?? 0}%` }} /></div>
              ))}
            </div>
            <div className="text-[10px] text-slate-500 mt-1">{s.improving}↑ {s.deteriorating}↓ of {s.n}</div>
          </button>
        ))}
      </div>
    </section>
  );
}

// ── aggregate cost-basis curves ─────────────────────────────────────────────
// Where the volume-weighted cost basis sits across horizons for the whole
// market, each sector, the indices and crypto: the median % distance of every
// horizon's VWAP from price. Below the line = that population is profitable.
function AggregateCurves({ curves, sector }: { curves: Record<string, Curve>; sector: string }) {
  const [group, setGroup] = useState<string>("equities");
  useEffect(() => { if (sector && curves[sector]) setGroup(sector); }, [sector, curves]);
  const keys = ["equities", "indices", "crypto", ...Object.keys(curves).filter(k => !["equities", "indices", "crypto"].includes(k))].filter(k => curves[k]);
  const c = curves[group] ?? curves[keys[0]];
  if (!c) return null;
  const W = 760, H = 220, P = 30;
  const vals = [...c.now, ...c.prior].map(p => p.rel_pct).filter((v): v is number => v != null);
  const lo = Math.min(0, ...vals), hi = Math.max(0, ...vals);
  const x = (i: number) => P + (i / (ORDER.length - 1)) * (W - 2 * P);
  const y = (v: number) => H - P - ((v - lo) / Math.max(1e-6, hi - lo)) * (H - 2 * P);
  const line = (pts: { h: string; rel_pct: number | null }[]) => pts.map((p, i) => p.rel_pct == null ? null : `${x(i).toFixed(1)},${y(p.rel_pct).toFixed(1)}`).filter(Boolean).join(" ");
  const last = c.now.filter(p => p.rel_pct != null);
  const above = last.filter(p => (p.rel_pct ?? 0) > 0).map(p => p.h);
  return (
    <section className="card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="label !mb-0">Aggregate cost-basis curve · where volume sits across horizons</h2>
        <select className="input !w-auto !py-1 text-xs" value={group} onChange={e => setGroup(e.target.value)}>
          {keys.map(k => <option key={k} value={k}>{SECTOR_NAMES[k] ?? k} ({curves[k].n})</option>)}
        </select>
      </div>
      <div className="grid gap-3 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] items-center">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="aggregate cost-basis curve">
          <line x1={P} x2={W - P} y1={y(0)} y2={y(0)} stroke="#f59e0b" strokeWidth="1.5" />
          <text x={W - P} y={y(0) - 4} textAnchor="end" fontSize="10" fill="#f59e0b">price</text>
          {ORDER.map((h, i) => <text key={h} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" className="fill-slate-500">{h}</text>)}
          <polyline points={line(c.prior)} fill="none" stroke="#64748b" strokeDasharray="3 3" strokeWidth="1.5" />
          <polyline points={line(c.now)} fill="none" stroke="#38bdf8" strokeWidth="2.5" />
          {c.now.map((p, i) => p.rel_pct == null ? null : (
            <g key={p.h}><circle cx={x(i)} cy={y(p.rel_pct)} r="3" fill="#38bdf8" />
              <text x={x(i)} y={y(p.rel_pct) + (p.rel_pct >= 0 ? -8 : 14)} textAnchor="middle" fontSize="9" className="fill-slate-300">{p.rel_pct > 0 ? "+" : ""}{p.rel_pct.toFixed(1)}%</text></g>
          ))}
        </svg>
        <p className="text-sm text-slate-400">
          Median distance of each horizon's VWAP from price across {c.n} names. Solid is now, dashed is an hour ago on the intraday horizons and five sessions ago on the daily ones.
          {above.length === 0 ? " Every holder population is under price: cost basis sits below at every horizon." :
            above.length === ORDER.length ? " Every holder population is above price: nobody who bought in the last year is ahead on the median name." :
            ` Price is below the cost basis of ${above.join(", ")} and above the rest; those horizons are the overhead supply.`}
        </p>
      </div>
    </section>
  );
}

// ── ask the analyst ─────────────────────────────────────────────────────────
function Ask() {
  const [q, setQ] = useState("");
  const [a, setA] = useState<{ answer: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try { setA(await api.post<{ answer: string }>("/api/intel/ask", { question: q })); } catch (x) { setA({ answer: String((x as Error).message || x) }); } finally { setBusy(false); }
  };
  const examples = ["Which names are structurally strong but too extended?", "Where is VWAP compression occurring?", "Which of our positions deteriorated since lunch?", "What should I be watching into the close?"];
  return (
    <section className="card space-y-2">
      <div className="flex gap-2">
        <input className="input" placeholder="Ask the analyst — answered from the scan, never from memory" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && void go()} />
        <button className="btn btn-primary" onClick={go} disabled={busy}>{busy ? "…" : "Ask"}</button>
      </div>
      <div className="flex flex-wrap gap-1">{examples.map(x => <button key={x} className="badge bg-ink text-slate-500 !font-normal hover:text-slate-300" onClick={() => setQ(x)}>{x}</button>)}</div>
      {a && <p className="text-sm text-slate-200 whitespace-pre-wrap">{a.answer}</p>}
    </section>
  );
}

// ── universe table ──────────────────────────────────────────────────────────
function UniverseTable({ rows, onPick }: { rows: Row[]; onPick: (s: string) => void }) {
  const [sort, setSort] = useState<keyof Row>("structure");
  const sorted = useMemo(() => [...rows].sort((a, b) => (Number(b[sort] ?? -1e9) - Number(a[sort] ?? -1e9))), [rows, sort]);
  const th = (k: keyof Row, label: string) => <th className={`text-left px-2 py-1 cursor-pointer ${sort === k ? "text-slate-200" : ""}`} onClick={() => setSort(k)}>{label}</th>;
  return (
    <section className="card overflow-x-auto">
      <h2 className="label">Coverage · {rows.length} names</h2>
      <table className="w-full text-sm tabular-nums">
        <thead className="text-xs text-slate-500">
          <tr>{th("symbol", "name")}{th("structure", "structure")}{th("d_structure", "Δ")}{th("entry", "entry")}{th("d_entry", "Δ")}{th("relvol", "relvol")}{th("compression_pctile", "compress")}
            <th className="text-left px-2">stage</th><th className="text-left px-2">30d 90d 360d</th><th className="text-left px-2">key level</th></tr>
        </thead>
        <tbody>
          {sorted.map(r => (
            <tr key={r.symbol} className="border-t border-edge hover:bg-ink/40 cursor-pointer" onClick={() => onPick(r.symbol)}>
              <td className="px-2 py-1 font-semibold">{r.symbol} {r.held_by.length > 0 && <span className="text-[10px] text-accent">held</span>}</td>
              <td className={`px-2 ${scoreTone(r.structure)}`}>{r.structure}</td>
              <td className={`px-2 ${r.d_structure > 0 ? "text-up" : r.d_structure < 0 ? "text-down" : "text-slate-600"}`}>{r.d_structure > 0 ? "+" : ""}{r.d_structure}</td>
              <td className={`px-2 ${scoreTone(r.entry)}`}>{r.entry}</td>
              <td className={`px-2 ${r.d_entry > 0 ? "text-up" : r.d_entry < 0 ? "text-down" : "text-slate-600"}`}>{r.d_entry > 0 ? "+" : ""}{r.d_entry}</td>
              <td className={`px-2 ${(r.relvol ?? 0) >= 1.5 ? "text-amber-300" : ""}`}>{fmt(r.relvol)}x</td>
              <td className={`px-2 ${(r.compression_pctile ?? 100) <= 10 ? "text-amber-300" : ""}`}>{r.compression_pctile == null ? "—" : `p${r.compression_pctile}`}</td>
              <td className="px-2 text-slate-400">{r.stage}</td>
              <td className="px-2 text-slate-400">{slopeGlyph(r.slopes["30d"])} {slopeGlyph(r.slopes["90d"])} {slopeGlyph(r.slopes["360d"])}</td>
              <td className="px-2 text-slate-400 whitespace-nowrap">{r.key_level ? `${r.key_level.members.join("/")} ${fmt(r.key_level.level)} (${r.key_level.dist_atr > 0 ? "+" : ""}${r.key_level.dist_atr} ATR)` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// ── one security ────────────────────────────────────────────────────────────
type Focus = "all" | "ribbon" | "structural" | "reclaim" | "compression" | "extension" | "failure";

function SecurityPanel({ symbol, row, onClose }: { symbol: string; row: Row | null; onClose: () => void }) {
  const [sec, setSec] = useState<Sec | null>(null);
  const [an, setAn] = useState<Analogs | null>(null);
  const [focus, setFocus] = useState<Focus>("all");
  const [families, setFamilies] = useState<{ ribbon: boolean; session: boolean; structural: boolean; anchors: boolean }>({ ribbon: true, session: true, structural: true, anchors: false });
  useEffect(() => {
    setSec(null); setAn(null);
    api.get<Sec>(`/api/intel/security/${encodeURIComponent(symbol)}`).then(setSec).catch(() => setSec({ symbol, state: null, as_of: null, score_path: [], timeline: [] }));
    api.get<Analogs>(`/api/intel/analogs/${encodeURIComponent(symbol)}`).then(setAn).catch(() => setAn(null));
  }, [symbol]);
  const st = sec?.state;
  const path = sec?.score_path ?? [];
  const samples = path.length > 4 ? [path[0], path[Math.floor(path.length / 3)], path[Math.floor((2 * path.length) / 3)], path[path.length - 1]] : path;
  return (
    <section className="card space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-baseline gap-3">
            <h2 className="text-2xl font-semibold">{symbol}</h2>
            {st && <span className="font-mono text-lg tabular-nums">{fmt(st.price)}</span>}
            {row && <span className="text-xs text-slate-500">{SECTOR_NAMES[row.sector ?? ""] ?? row.sector} · {row.roles.join(", ")}{row.held_by.length ? ` · held in ${row.held_by.join(", ")}` : ""}</span>}
          </div>
          {st && <p className="text-sm text-slate-300 max-w-3xl mt-1">{st.narrative?.why}</p>}
        </div>
        <button className="btn" onClick={onClose}>close</button>
      </div>
      {!sec && <p className="text-sm text-slate-500">reading the bars…</p>}
      {sec && !st && <p className="text-sm text-slate-500">No scan on record for {symbol}. Run a scan first.</p>}
      {st && (
        <>
          <div className="grid gap-3 md:grid-cols-4">
            <ScoreTile label="Structure" value={st.structure} path={samples.map(p => p[1])} parts={st.structure_parts} />
            <ScoreTile label="Entry quality" value={st.entry} path={samples.map(p => p[2])} parts={st.entry_parts} />
            <div className="card !p-3">
              <div className="label">Priority · stage</div>
              <div className={`text-lg font-semibold ${st.priority === "immediate" ? "text-up" : st.priority === "monitor" ? "text-amber-300" : "text-slate-300"}`}>{st.priority}</div>
              <div className="text-sm text-slate-400">{st.progression?.stage} · {st.alignment?.label}</div>
              <div className="text-xs text-slate-500 mt-1">chain holds through {st.alignment?.aligned_through ?? "nothing"}{st.alignment?.breaks?.length ? `, breaks at ${st.alignment.breaks[0]}` : ""}</div>
            </div>
            <div className="card !p-3">
              <div className="label">Key level</div>
              {st.key_level ? (
                <>
                  <div className="font-mono text-lg tabular-nums">{fmt(st.key_level.level)}</div>
                  <div className="text-sm text-slate-400">{st.key_level.members.join(" / ")} {st.key_level.kind}</div>
                  <div className="text-xs text-slate-500">{Math.abs(st.key_level.dist_atr)} ATR {st.key_level.side}</div>
                </>
              ) : <div className="text-sm text-slate-500">none within reach</div>}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-slate-500">story chart:</span>
            {(["all", "reclaim", "compression", "extension", "failure"] as Focus[]).map(f => (
              <button key={f} onClick={() => setFocus(f)} className={`badge ${focus === f ? "bg-accent text-white" : "bg-ink text-slate-400"} !font-normal`}>{f === "all" ? "everything" : `the ${f}`}</button>
            ))}
            <span className="text-slate-600">·</span>
            {(["ribbon", "session", "structural", "anchors"] as const).map(k => (
              <label key={k} className="flex items-center gap-1 text-slate-400"><input type="checkbox" checked={families[k]} onChange={e => setFamilies({ ...families, [k]: e.target.checked })} />{k}</label>
            ))}
            <ExportButton targetId={`chart-${symbol}`} name={`${symbol}-${focus}`} />
          </div>
          {sec.chart ? <VwapChart id={`chart-${symbol}`} symbol={symbol} chart={sec.chart} state={st} focus={focus} families={families} /> :
            <p className="text-sm text-slate-500">{sec.chart_error ? `chart unavailable: ${sec.chart_error}` : "no bars"}</p>}

          <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
            <div className="space-y-4 min-w-0">
              <TermStructure state={st} />
              <Timeline events={sec.timeline} />
              {an && an.sample > 0 && (
                <div className="card !p-3">
                  <div className="label">Historical analogs · same alignment, stage and sides</div>
                  <p className="text-sm">{an.sample} prior snapshots resembled this. Five sessions later the mean move was <span className={`font-mono ${(an.mean_fwd_pct ?? 0) >= 0 ? "text-up" : "text-down"}`}>{fmt(an.mean_fwd_pct)}%</span>, positive {an.positive_pct}% of the time.</p>
                  <p className="text-xs text-slate-500 mt-1">{an.matches.slice(0, 6).map(m => `${m.symbol} ${m.day} (${m.fwd_pct == null ? "—" : fmt(m.fwd_pct) + "%"})`).join(" · ")}</p>
                </div>
              )}
            </div>
            <Output state={st} />
          </div>
        </>
      )}
    </section>
  );
}

function ScoreTile({ label, value, path, parts }: { label: string; value: number; path: number[]; parts: Record<string, number> }) {
  return (
    <div className="card !p-3">
      <div className="label">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className={`font-mono text-3xl tabular-nums ${scoreTone(value)}`}>{value}</span>
        {path.length > 1 && <span className="font-mono text-xs text-slate-500 tabular-nums">{path.slice(0, -1).join(" → ")} → {value}</span>}
      </div>
      <div className="flex gap-0.5 mt-2" title={Object.entries(parts).map(([k, v]) => `${k} ${v}`).join(" · ")}>
        {Object.entries(parts).map(([k, v]) => <div key={k} className="h-1.5 rounded-sm bg-accent/60" style={{ width: `${Math.max(1, v)}%` }} title={`${k} ${v}`} />)}
      </div>
    </div>
  );
}

function Output({ state: st }: { state: State }) {
  const n = st.narrative ?? {};
  const fields: [string, string][] = [
    ["VWAP structure", n.structure], ["Change", n.change], ["Volume", n.volume], ["Retest", n.retest], ["Longer-term structure", n.longer_term],
    ["Extension", n.extension], ["Key level", n.key_level], ["What improves the structure", st.improves], ["What damages the structure", st.damages], ["Why it matters", n.why],
  ];
  return (
    <div className="card !p-3 space-y-2 min-w-0">
      <div className="label">The read · every number is the engine's</div>
      {fields.map(([k, v]) => (
        <div key={k} className="grid grid-cols-[120px_1fr] gap-2 text-sm">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 pt-0.5">{k}</span>
          <span className="text-slate-300">{v || "—"}</span>
        </div>
      ))}
      <div className="grid grid-cols-[120px_1fr] gap-2 text-sm">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 pt-0.5">Slopes</span>
        <span className="text-slate-300 font-mono text-xs">{ORDER.map(h => `${h} ${slopeGlyph(st.slopes?.[h])}`).join("  ")}</span>
      </div>
      {st.anchors?.length > 0 && (
        <div className="grid grid-cols-[120px_1fr] gap-2 text-sm">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 pt-0.5">Anchored</span>
          <span className="text-slate-300 text-xs">{st.anchors.map((a: { kind: string; date: string; vwap: number; dist_pct: number }) => `${a.kind} (${a.date}) ${fmt(a.vwap)} ${a.dist_pct >= 0 ? "+" : ""}${fmt(a.dist_pct, 1)}%`).join(" · ")}</span>
        </div>
      )}
    </div>
  );
}

function Timeline({ events }: { events: Ev[] }) {
  if (!events.length) return <div className="card !p-3"><div className="label">Intelligence timeline</div><p className="text-sm text-slate-500">no events on record yet</p></div>;
  return (
    <div className="card !p-3">
      <div className="label">Intelligence timeline</div>
      <ul className="space-y-1 max-h-72 overflow-y-auto">
        {events.slice().reverse().map(e => (
          <li key={e.id} className="text-sm flex gap-2">
            <span className="font-mono text-xs text-slate-500 tabular-nums whitespace-nowrap">{etDay(e.at)} {et(e.at)}</span>
            <span className={`font-mono text-xs ${sig(e.significance)}`}>{e.significance}</span>
            <span className="text-slate-300">{e.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TermStructure({ state: st }: { state: State }) {
  const cur: { h: string; rel_pct: number | null }[] = st.term_structure ?? [];
  const prior: { h: string; rel_pct: number | null }[] = st.prior_term_structure?.curve ?? [];
  const vals = [...cur, ...prior].map(p => p.rel_pct).filter((v): v is number => v != null);
  if (!vals.length) return null;
  const W = 420, H = 160, P = 24;
  const lo = Math.min(0, ...vals), hi = Math.max(0, ...vals);
  const x = (i: number) => P + (i / (ORDER.length - 1)) * (W - 2 * P);
  const y = (v: number) => H - P - ((v - lo) / Math.max(1e-6, hi - lo)) * (H - 2 * P);
  const line = (pts: { h: string; rel_pct: number | null }[]) => pts.map((p, i) => p.rel_pct == null ? null : `${x(i).toFixed(1)},${y(p.rel_pct).toFixed(1)}`).filter(Boolean).join(" ");
  return (
    <div className="card !p-3">
      <div className="label">VWAP term structure · cost basis vs price, by horizon</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="VWAP term structure">
        <line x1={P} x2={W - P} y1={y(0)} y2={y(0)} stroke="#475569" />
        <text x={W - P} y={y(0) - 3} textAnchor="end" fontSize="9" className="fill-slate-500">price</text>
        {ORDER.map((h, i) => <text key={h} x={x(i)} y={H - 6} textAnchor="middle" fontSize="9" className="fill-slate-500">{h}</text>)}
        <polyline points={line(prior)} fill="none" stroke="#64748b" strokeDasharray="3 3" strokeWidth="1.2" />
        <polyline points={line(cur)} fill="none" stroke="#38bdf8" strokeWidth="2" />
        {cur.map((p, i) => p.rel_pct == null ? null : <circle key={p.h} cx={x(i)} cy={y(p.rel_pct)} r="2.5" fill="#38bdf8" />)}
      </svg>
      <p className="text-xs text-slate-500">Solid = now. Dashed = 60 minutes ago (intraday) / 5 sessions ago (daily). Below the line = cost basis under price (holders profitable).</p>
    </div>
  );
}

// ── the chart ───────────────────────────────────────────────────────────────
function VwapChart({ id, symbol, chart, state: st, focus, families }: { id: string; symbol: string; chart: Chart; state: State; focus: Focus;
  families: { ribbon: boolean; session: boolean; structural: boolean; anchors: boolean } }) {
  const W = 980, H = 420, PL = 8, PR = 64, PT = 10, PB = 60, VH = 48;
  // today's session = the bars sharing the last bar's ET date
  const last = chart.minutes[chart.minutes.length - 1];
  const dayOf = (t: string) => new Date(t).toLocaleDateString("en-US", { timeZone: "America/New_York" });
  const today = last ? dayOf(last.t) : "";
  const startIdx = Math.max(0, chart.minutes.findIndex(b => dayOf(b.t) === today));
  const bars = chart.minutes.slice(startIdx);
  const slice = (xs: (number | null)[]) => xs.slice(xs.length - bars.length);
  const n = bars.length || 1;
  const vis = {
    ribbon: families.ribbon && focus !== "extension" ? ["30m", "60m", "90m", "120m", "360m"] : focus === "compression" ? ["30m", "60m", "90m", "120m"] : focus === "extension" ? ["30m"] : [],
    session: families.session || focus === "reclaim" || focus === "failure" || focus === "extension",
    structural: families.structural && focus !== "compression" ? DAILY : focus === "extension" ? ["5d"] : focus === "reclaim" || focus === "failure" ? DAILY.filter(h => st.key_level?.members?.includes(h)) : [],
  };
  const keyMembers: string[] = st.key_level?.members ?? [];
  const lvls: [string, number][] = vis.structural.map(h => [h, chart.daily_levels[h]] as [string, number | null]).filter((p): p is [string, number] => p[1] != null);
  const anchorLv: [string, number][] = families.anchors ? (st.anchors ?? []).map((a: { kind: string; vwap: number }) => [a.kind, a.vwap] as [string, number]) : [];
  const lows = bars.map(b => b.l), highs = bars.map(b => b.h);
  let lo = Math.min(...lows), hi = Math.max(...highs);
  const range = hi - lo || 1;
  // keep structural levels inside ±1.5 ranges of the session; beyond that they are labeled at the edge
  const inRange = [...lvls, ...anchorLv].map(p => p[1]).filter(v => v > lo - 1.5 * range && v < hi + 1.5 * range);
  if (inRange.length) { lo = Math.min(lo, ...inRange); hi = Math.max(hi, ...inRange); }
  const pad = (hi - lo) * 0.06; lo -= pad; hi += pad;
  const x = (i: number) => PL + ((i + 0.5) / n) * (W - PL - PR);
  const y = (v: number) => PT + ((hi - v) / (hi - lo)) * (H - PT - PB);
  const bw = Math.max(1, ((W - PL - PR) / n) * 0.7);
  const prof = (chart.volume_profile ?? []).slice(0, bars.length);
  const sortedV = [...bars.map(b => b.v), ...prof].sort((a, b) => a - b);
  const vmax = Math.max(1, sortedV[Math.floor(sortedV.length * 0.98)] ?? 1);
  const vy = (v: number) => H - 8 - (Math.min(v, vmax) / vmax) * VH;
  const linePts = (xs: (number | null)[]) => slice(xs).map((v, i) => v == null ? null : `${x(i).toFixed(1)},${y(v).toFixed(1)}`).filter(Boolean).join(" ");
  const ribbonWidth = (h: string) => h === "30m" ? 1.6 : h === "360m" ? 1.2 : 1;
  const ribbonOpacity = (h: string) => h === "30m" ? 0.95 : h === "60m" ? 0.75 : h === "90m" ? 0.6 : h === "120m" ? 0.5 : 0.7;
  const sess = chart.intraday.session;
  const tick = (i: number) => et(bars[i].t);
  const keyY = st.key_level ? y(st.key_level.level) : null;
  return (
    <div className="overflow-x-auto">
      <svg id={id} viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[720px]" role="img" aria-label={`${symbol} VWAP chart`} style={{ background: "#0b1220" }}>
        {/* grid */}
        {[0.2, 0.4, 0.6, 0.8].map(f => <line key={f} x1={PL} x2={W - PR} y1={PT + f * (H - PT - PB)} y2={PT + f * (H - PT - PB)} stroke="#1e293b" />)}
        {/* key-level zone */}
        {keyY != null && (focus === "all" || focus === "reclaim" || focus === "failure") && st.key_level.kind === "cluster" && (
          <rect x={PL} width={W - PL - PR} y={Math.min(y(st.key_level.high ?? st.key_level.level), y(st.key_level.low ?? st.key_level.level)) - 2}
            height={Math.abs(y(st.key_level.low ?? st.key_level.level) - y(st.key_level.high ?? st.key_level.level)) + 4} fill="#fbbf24" fillOpacity="0.07" />
        )}
        {/* volume */}
        {bars.map((b, i) => <rect key={i} x={x(i) - bw / 2} width={bw} y={vy(b.v)} height={H - 8 - vy(b.v)} fill={b.c >= b.o ? "#22c55e" : "#ef4444"} fillOpacity="0.35" />)}
        {prof.length > 1 && <polyline points={prof.map((v, i) => `${x(i).toFixed(1)},${vy(v).toFixed(1)}`).join(" ")} fill="none" stroke="#94a3b8" strokeWidth="1.2" />}
        {prof.length > 1 && <text x={PL + 4} y={H - 8 - VH - 3} fontSize="9" fill="#64748b">volume · grey line = normal for this time of day</text>}
        {/* structural lines */}
        {lvls.map(([h, v]) => {
          const yy = Math.max(PT + 4, Math.min(H - PB - 4, y(v)));
          const clipped = yy !== y(v);
          const hot = keyMembers.includes(h);
          return (
            <g key={h}>
              <line x1={PL} x2={W - PR} y1={yy} y2={yy} stroke={RIBBON_COLOR[h]} strokeOpacity={hot ? 0.9 : 0.5} strokeWidth={hot ? 1.6 : 1} strokeDasharray={clipped ? "2 4" : h === "5d" ? "6 3" : undefined} />
              <text x={W - PR + 4} y={yy + 3} fontSize="10" fill={RIBBON_COLOR[h]}>{h} {fmt(v)}{clipped ? (y(v) < PT ? " ▲" : " ▼") : ""}</text>
            </g>
          );
        })}
        {anchorLv.map(([k, v]) => {
          const yy = Math.max(PT + 4, Math.min(H - PB - 4, y(v)));
          return <g key={k}><line x1={PL} x2={W - PR} y1={yy} y2={yy} stroke="#c084fc" strokeOpacity="0.6" strokeDasharray="1 3" /><text x={PL + 4} y={yy - 3} fontSize="9" fill="#c084fc">{k} {fmt(v)}</text></g>;
        })}
        {/* ribbon */}
        {vis.ribbon.map(h => chart.intraday[h] && <polyline key={h} points={linePts(chart.intraday[h])} fill="none" stroke={RIBBON_COLOR[h]} strokeOpacity={ribbonOpacity(h)} strokeWidth={ribbonWidth(h)} />)}
        {vis.session && sess && <polyline points={linePts(sess)} fill="none" stroke={RIBBON_COLOR.session} strokeWidth="1.8" />}
        {/* candles */}
        {bars.map((b, i) => (
          <g key={i}>
            <line x1={x(i)} x2={x(i)} y1={y(b.h)} y2={y(b.l)} stroke={b.c >= b.o ? "#4ade80" : "#f87171"} strokeWidth="1" />
            <rect x={x(i) - bw / 2} width={bw} y={y(Math.max(b.o, b.c))} height={Math.max(1, Math.abs(y(b.o) - y(b.c)))} fill={b.c >= b.o ? "#4ade80" : "#f87171"} />
          </g>
        ))}
        {/* extension shading: distance from session VWAP */}
        {focus === "extension" && sess && last && (() => { const sv = slice(sess); const v = sv[sv.length - 1]; return v == null ? null : (
          <g><line x1={x(n - 1)} x2={x(n - 1)} y1={y(last.c)} y2={y(v)} stroke="#f59e0b" strokeWidth="2" strokeDasharray="4 2" />
            <text x={x(n - 1) - 6} y={(y(last.c) + y(v)) / 2} textAnchor="end" fontSize="11" fill="#f59e0b">{st.rel?.session?.dist_atr != null ? `${st.rel.session.dist_atr > 0 ? "+" : ""}${st.rel.session.dist_atr} ATR from session VWAP` : ""}</text></g>); })()}
        {/* axis */}
        {bars.length > 0 && [0, Math.floor(n / 4), Math.floor(n / 2), Math.floor((3 * n) / 4), n - 1].map(i => <text key={i} x={x(i)} y={H - PB + 14} textAnchor="middle" fontSize="10" fill="#64748b">{tick(i)}</text>)}
        <text x={W - PR - 4} y={PT + 12} textAnchor="end" fontSize="11" fill="#e2e8f0">{symbol} · {fmt(last?.c)} · S {st.structure} / E {st.entry}</text>
        {/* legend for the ribbon */}
        <g transform={`translate(${PL + 6},${PT + 6})`}>
          {[...vis.ribbon, ...(vis.session ? ["session"] : [])].map((h, i) => <g key={h} transform={`translate(${i * 56},0)`}><line x1={0} x2={14} y1={4} y2={4} stroke={RIBBON_COLOR[h]} strokeWidth="2" /><text x={17} y={8} fontSize="9" fill="#94a3b8">{h}</text></g>)}
        </g>
      </svg>
    </div>
  );
}

function ExportButton({ targetId, name }: { targetId: string; name: string }) {
  const busy = useRef(false);
  const go = () => {
    if (busy.current) return;
    const svg = document.getElementById(targetId);
    if (!svg) return;
    busy.current = true;
    const xml = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    const url = URL.createObjectURL(new Blob([xml], { type: "image/svg+xml;charset=utf-8" }));
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = 1960; c.height = 840;
      const ctx = c.getContext("2d");
      if (ctx) { ctx.fillStyle = "#0b1220"; ctx.fillRect(0, 0, c.width, c.height); ctx.drawImage(img, 0, 0, c.width, c.height); }
      const a = document.createElement("a");
      a.href = c.toDataURL("image/png"); a.download = `${name}.png`; a.click();
      URL.revokeObjectURL(url); busy.current = false;
    };
    img.onerror = () => { busy.current = false; URL.revokeObjectURL(url); };
    img.src = url;
  };
  return <button className="badge bg-ink text-slate-400 !font-normal hover:text-slate-200 ml-auto" onClick={go}>export story chart (PNG)</button>;
}

// ── briefs ──────────────────────────────────────────────────────────────────
function Briefs() {
  const [kind, setKind] = useState<"morning" | "eod">("morning");
  const [b, setB] = useState<Brief | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get<Brief>(`/api/intel/brief?kind=${kind}`).then(setB).catch(() => setB(null)), [kind]);
  useEffect(() => { void load(); }, [load]);
  const build = async () => { setBusy(true); try { setB(await api.post<Brief>(`/api/intel/brief?kind=${kind}`)); } finally { setBusy(false); } };
  const d = (b?.data ?? {}) as Record<string, unknown[]>;
  const charts = ((b?.data ?? {}) as { charts?: { market?: string; quadrant?: string } }).charts ?? {};
  const cards = (((b?.data ?? {}) as { cards?: Card[] }).cards ?? []);
  const section = (title: string, key: string, render: (r: Record<string, unknown>) => string) => (
    <div>
      <div className="label">{title}</div>
      {!(d[key] as unknown[] | undefined)?.length ? <p className="text-sm text-slate-600">nothing in this section</p> :
        <ul className="text-sm space-y-0.5">{(d[key] as Record<string, unknown>[]).slice(0, 10).map((r, i) => <li key={i} className="text-slate-300">{render(r)}</li>)}</ul>}
    </div>
  );
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <nav className="flex rounded-lg border border-edge overflow-hidden text-sm">
          {(["morning", "eod"] as const).map(k => <button key={k} onClick={() => setKind(k)} className={`px-3 py-1.5 ${kind === k ? "bg-panel text-slate-100" : "text-slate-500"}`}>{k === "morning" ? "Morning brief" : "End of day"}</button>)}
        </nav>
        <button className="btn" onClick={build} disabled={busy}>{busy ? "Building…" : "Build now"}</button>
        {b?.day && <span className="text-xs text-slate-500">{b.day}{b.created_at ? ` · ${et(b.created_at)} ET` : ""}</span>}
      </div>
      {!b?.text && <p className="text-sm text-slate-500">No {kind} brief on record. The morning brief mails at 08:30 ET and the end-of-day assessment is built at 16:20 ET.</p>}
      {b?.text && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <article className="card space-y-3 text-[15px] leading-relaxed text-slate-200">
            {b.text.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
            {charts.market && <img src={charts.market} alt="market structure" className="w-full rounded-lg border border-edge bg-white" />}
            {charts.quadrant && <img src={charts.quadrant} alt="structure by entry" className="w-full rounded-lg border border-edge bg-white" />}
            {cards.length > 0 && <h3 className="label !mt-4">Where the cost basis is heading · asset by asset</h3>}
            {cards.map(c => (
              <div key={c.symbol} className="border border-edge rounded-lg p-3 space-y-2">
                <div className="font-semibold">{c.symbol} <span className="text-xs text-slate-500 font-normal">· {c.shape} · structure {c.structure} / entry {c.entry}</span></div>
                {c.chart && <img src={c.chart} alt={`${c.symbol} story chart`} className="w-full rounded border border-edge bg-white" />}
                {c.commentary && <p className="text-sm text-slate-300">{c.commentary}</p>}
                {c.prediction?.text && (
                  <p className="text-sm border-l-2 border-amber-400 pl-2"><b>Call:</b> {c.prediction.text}
                    <span className="block text-xs text-slate-500">Confirms: {c.prediction.confirms} · Contradicts: {c.prediction.contradicts}</span></p>
                )}
              </div>
            ))}
          </article>
          <div className="card space-y-4 text-sm">
            {section("Major structural changes", "major_changes", r => `${r.symbol} — ${r.text}`)}
            {section("Decision zones", "decision_zones", r => `${r.symbol} ${fmt(r.level as number)} (${(r.members as string[]).join("/")}, ${r.dist_atr} ATR ${r.side})`)}
            {section("Developing structures", "developing", r => `${r.symbol} ${r.structure}/${r.entry} ${(r.d_structure as number) >= 0 ? "+" : ""}${r.d_structure} · ${r.stage}`)}
            {section("Strong but extended", "strong_but_extended", r => `${r.symbol} structure ${r.structure}, entry ${r.entry}`)}
            {section("Quality retests", "quality_retests", r => `${r.symbol} — ${r.text}`)}
            {section("Failures", "failures", r => `${r.symbol} ${r.text ?? `structure ${(r.d_structure as number) >= 0 ? "+" : ""}${r.d_structure}`}`)}
            {section(kind === "eod" ? "Biggest improvements" : "Improving since yesterday", "improvements", r => `${r.symbol} ${r.structure} (${(r.d_structure as number) >= 0 ? "+" : ""}${r.d_structure})`)}
            {section("Biggest deteriorations", "deteriorations", r => `${r.symbol} ${r.structure} (${(r.d_structure as number) >= 0 ? "+" : ""}${r.d_structure})`)}
            {section("Pullbacks worth a look", "pullbacks", r => `${r.symbol} structure ${r.structure}, entry ${r.entry} (+${r.d_entry})`)}
            {section("Research flags", "research_flags", r => `${r.symbol} relvol ${fmt(r.relvol as number)}x`)}
            {section("What matters", "what_matters", r => `${r.symbol} — ${r.why}`)}
          </div>
        </div>
      )}
    </section>
  );
}

// ── research ────────────────────────────────────────────────────────────────
function ResearchPanel() {
  const [r, setR] = useState<Research | null>(null);
  useEffect(() => { api.get<Research>("/api/intel/research?days=90").then(setR).catch(() => setR(null)); }, []);
  if (!r) return <p className="text-sm text-slate-500">loading…</p>;
  return (
    <section className="card overflow-x-auto">
      <h2 className="label">Signal usefulness · {r.events} events in {r.days} days, {r.graded_5d} graded at five sessions</h2>
      <p className="text-sm text-slate-400 mb-2">A hit is the market moving in the event's own direction. Neutral kinds (compression, volume, extended) are measured by mean move only. Grading runs nightly; a kind with no graded rows is too young to judge.</p>
      <table className="w-full text-sm tabular-nums">
        <thead className="text-xs text-slate-500"><tr><th className="text-left px-2">kind</th><th className="text-left px-2">n</th><th className="text-left px-2">graded</th><th className="text-left px-2">mean +1d</th><th className="text-left px-2">hit +1d</th><th className="text-left px-2">mean +5d</th><th className="text-left px-2">hit +5d</th></tr></thead>
        <tbody>
          {r.kinds.map(k => (
            <tr key={k.kind} className="border-t border-edge">
              <td className="px-2 py-1">{KIND_LABEL[k.kind] ?? k.kind} <span className="text-slate-600">{k.direction > 0 ? "bullish" : k.direction < 0 ? "bearish" : "neutral"}</span></td>
              <td className="px-2">{k.n}</td><td className="px-2">{k.graded_5d}</td>
              <td className={`px-2 ${(k.mean_1d ?? 0) >= 0 ? "text-up" : "text-down"}`}>{k.mean_1d == null ? "—" : `${fmt(k.mean_1d)}%`}</td>
              <td className="px-2">{k.hit_1d == null ? "—" : `${k.hit_1d}%`}</td>
              <td className={`px-2 ${(k.mean_5d ?? 0) >= 0 ? "text-up" : "text-down"}`}>{k.mean_5d == null ? "—" : `${fmt(k.mean_5d)}%`}</td>
              <td className="px-2">{k.hit_5d == null ? "—" : `${k.hit_5d}%`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
